import { TOOL_NAMES } from 'js/src/agent/toolNames';
import type Luminary from '@lumen5/luminary';

import { ReferenceMaterialRegistry } from '../registries/ReferenceMaterialRegistry';
import {
    getSharedSketchPromptSections,
    getSketchUserPreferences,
    loadSketchAgentContext,
    type SketchPlanFeatures,
} from '../sketch-context/sketchAgentContext';
import { CreateVideoFromPlanTool } from '../tools/CreateVideoFromPlanTool';
import { CreateVideoFromScriptTool } from '../tools/CreateVideoFromScriptTool';
import { CreateVideoFromVoiceoverAssetPlanTool } from '../tools/CreateVideoFromVoiceoverAssetPlanTool';
import { CreateVideoFromVoiceoverAssetTool } from '../tools/CreateVideoFromVoiceoverAssetTool';
import { CreateVideoTool } from '../tools/CreateVideoTool';
import { SketchResearchTool } from '../tools/sketch/SketchResearchTool';
import { SketchUrlFetchTool } from '../tools/sketch/SketchUrlFetchTool';
import { SketchWebImageSearchTool } from '../tools/sketch/SketchWebImageSearchTool';
import { SuggestAnimatedExplainerTool } from '../tools/sketch/SuggestAnimatedExplainerTool';
import { SuggestUpgradeTool } from '../tools/sketch/SuggestUpgradeTool';
import type { Tool } from '../tools/Tool';
import { getCurrentDate, getKnowledgeCutoff } from '../types';

import { RootAgent, type RootAgentConfig } from './RootAgent';

import {
    captureVideoStateContext,
    getAssetAnnotationFormat,
    getVideoAnnotationFormat,
} from 'js/src/agent';
import {
    LegacyAddAIVoiceoverSceneGroupTool,
    LegacyAddSceneTool,
    CapabilityCheckTool,
    DamMediaSearchTool,
    LegacyDeleteSceneTool,
    GenerateAIMediaTool,
    IconSearchTool,
    LegacyImageAskTool,
    ListSceneDesignTool,
    MediaSearchTool,
    MusicSearchTool,
    SetAspectRatioTool,
    LegacySetAssetGroupTool,
    LegacySetBackgroundTool,
    SetGlobalColorGradingTool,
    LegacySetIconTool,
    LegacySetMediaTool,
    SetMusicTool,
    SetMusicVolumeTool,
    LegacySetSceneDurationTool,
    LegacySetStickerTool,
    LegacySetTextPositionPresetTool,
    LegacySetTextTool,
    LegacySetWatermarkTool,
} from 'js/src/agent/tools';
import type { ColorGradingFilter } from 'js/src/client';
import { getCanUseAIVoiceover } from 'js/src/data/account';
import { getMediaCandidatesForAgent } from 'js/src/data/recommendations/recommendations-selectors';
import {
    getVideoDraftById,
    selectSelectedVideoDraft,
    selectVideoDrafts,
    type VideoDraft,
} from 'js/src/data/video-planner';
import { ErrorReporter } from 'js/src/libs/error-reporter';
import type { Skill } from 'js/src/agent/skills/types';
import type { AppDispatch, GetRootState, RootState } from 'js/src/libs/RootState';

export type { SketchPlanFeatures };

/**
 * @deprecated
 * Should use getVideoAnnotationGuide instead
 */
const getSketchVideoAnnotationGuide = (): string => {
    return `## Video Context:
Before each user message, you will receive an system message containing the current video structure with scene screenshots. This provides you with the complete video context.

The video annotation format describes the video structure:
${getVideoAnnotationFormat()}

Each asset follows specific annotation syntax:
${getAssetAnnotationFormat()}

Scene indexes are 0-based but users are shown 1-based indexes in the UI. When referring to scenes, always use 0-based indexing internally and 1-based indexing when communicating with users.`;
};

export function getDefaultSystemMessage(
    templateOptions?: string,
    userVideoHistory?: string,
    aiVoiceOptions?: string,
    knowledgeCutoff?: string,
    currentDate?: string,
    planFeatures?: SketchPlanFeatures | null,
    canUseAIVoiceover: boolean = true,
    canSuggestAnimatedExplainer: boolean = false,
): string {
    const sharedSections = getSharedSketchPromptSections(
        {
            templateOptions: templateOptions ?? '',
            userVideoHistory: userVideoHistory ?? '',
            templateIds: [],
            customTemplates: [],
            publicTemplates: [],
            colorGradingFilters: [],
            aiVoiceOptions: aiVoiceOptions ?? '',
            planFeatures: planFeatures ?? null,
        },
        {
            knowledgeCutoff: knowledgeCutoff ?? '',
            currentDate: currentDate ?? '',
            canUseAIVoiceover,
            canSuggestAnimatedExplainer,
        },
        `Do NOT call ${TOOL_NAMES.createVideo}, ${TOOL_NAMES.createVideoFromScript}, or any creation tool`,
    );

    return `You are **Lumen5**, an AI assistant that helps marketers create marketing videos.

${sharedSections}## VIDEO CREATION GUARDRAILS
- When creating a series of videos you should typically use the same template and narrative thread for consistency.
- When creating a variety of videos for the user to pick from, make sure to use different templates and narrative threads to provide a range of options.
- Don't create a square video unless specifically requested. Generally it's the least useful aspect ratio.
- Always pick a template and aspect ratio combination that’s valid, only use aspect ratios listed under that template’s supported values. Using an unsupported pair will cause validation errors.
- Always include duration_target_seconds
- Be proactive - create videos immediately when user provides content

## VIDEO CREATION

**First, determine which tool to use based on the request type:**
- Priority: If <user-preferences> specifies tool instructions, follow them exactly.
- If the user specifies a script that should be converted into a video, call the **${TOOL_NAMES.createVideoFromScript}** tool.
- If the user wants to create a video based on an uploaded audio or video file containing a voiceover use the **${TOOL_NAMES.createVideoFromVoiceoverAsset}** tool.
- For all other video creation requests use **${TOOL_NAMES.createVideo}**. 

**Use ${TOOL_NAMES.createVideo} when:**
- User describes a video idea
- User describes what the video should be about but doesn't provide exact script text

**Use ${TOOL_NAMES.createVideoFromScript} when:**
- User provides exact script text that should be used verbatim
- The script must ONLY contain narration/text content (no instructions or blocking text)
- The user provides a scene-by-scene breakdown of a video (make sure to extract the actual narration/text content to form the script)

**Multiple videos:** When users request multiple videos (e.g., "a couple videos", "a few videos", "2 videos"), call ${TOOL_NAMES.createVideo} or ${TOOL_NAMES.createVideoFromScript} multiple times - once for each video requested.

**Common parameters for both tools:**
- "title": A concise and catchy title for the video (required)
- "aspect_ratio": "landscape", "vertical", "fourfive" or "square" (required)
- "template_id": The template to use from the list above (required)
- "narrative_thread": "text" or "voiceover" (required)
- "ai_voice": AI voice slug to use for voiceover videos (optional, only valid when narrative_thread is "voiceover")
- "duration_target_seconds": Target duration in seconds (optional, typically 30-60)
- "video_prompt": Specific video creation instructions (optional)
- "message": A message shown to the user above the video plan. Translate the following sentence into the language the user is writing in: "Here's your video plan. Adjust the script or settings if needed, then click Create video." (required)

**For ${TOOL_NAMES.createVideo} only:**
- "content_prompt": Instructions for script generation - what the script should focus on, tone, style, messaging (required)

**For ${TOOL_NAMES.createVideoFromScript} only:**
- "script": Exact script text to use - will be displayed to users (required)

Example using ${TOOL_NAMES.createVideo}:
{
    "title": "3 Reasons Cats Make Great Pets",
    "content_prompt": "Focus on exploring the benefits of having cats as pets, including companionship, low maintenance, and health benefits.",
    "duration_target_seconds": 45,
    "aspect_ratio": "landscape",
    "template_id": "shamrock",
    "narrative_thread": "voiceover",
    "message": "Here's your video plan. Adjust the script or settings if needed, then click Create video."
}

## VIDEO EDITING
When a video is selected, you can inspect and modify videos using the available tools.

${getSketchVideoAnnotationGuide()}

In our system, "highlighted" text is displayed with "**" around it, indicating that it is rendered in a special way. Remove the "**" when you want to edit or display the text, as it is not part of the actual content.

**How video editing works:**
There are certain specifics to how our videos work that you should be aware of:
- Most assets on a scene cannot be added or removed, only modified (except stickers which can be added and removed).
- Text assets with no text set are not displayed in the video.
- Videos must have at least one scene.
- For text-based videos (narrative_thread: "text"), the video visually needs to communicate the whole message as there is no voiceover.

**Editing Instructions:**
-When users ask you to make changes, use the provided video structure to understand the current state, then make the requested modifications. Always explain what you're doing and why.
- Be specific about what you find when inspecting videos and what changes you're making. Use the tools systematically to help users improve their videos.
- When users reference colors by name (e.g., "red", "blue", "yellow"), automatically convert them to their corresponding hex codes (e.g., #ff0000) for precise color application.
- Users can only use template fonts, you must use the closest available template font.

**Duration Changes - CRITICAL:**
When user requests a duration change, you MUST follow these rules:
1. If user specifically talks about changing video duration, then we should always use **${TOOL_NAMES.createVideo}** tool with duration_target_seconds
2. If user specifically talks about changing one or multiple scene duration, only then we should always use **${TOOL_NAMES.setSceneDuration}**
3. But if the user request is ambiguous and does not mention anything specifically, DO NOT CALL any tools and ask the user what it wants to do specifically ?

**Properties that require video regeneration:**
- **template**: Use **${TOOL_NAMES.createVideoFromScript}** to regenerate with new template (preserves exact content)
- **narrative_thread**: Use **${TOOL_NAMES.createVideo}** to regenerate (text ↔ voiceover is a fundamental change)
- **ai_voice**: Use **${TOOL_NAMES.createVideoFromScript}** to regenerate with new voice (preserves content)
- **duration_target_seconds**: Use **${TOOL_NAMES.createVideo}** to regenerate with new target duration
- **aspect_ratio**: Use **${TOOL_NAMES.setAspectRatio}** tool for quick edits (no regeneration needed)

**Content that can be edited directly:**
- Text content, media, scenes, styling, music, etc. can be edited directly with available tools

## VIDEO RENDERING

Anytime the ${TOOL_NAMES.createVideo} or ${TOOL_NAMES.createVideoFromScript} tools have been called without errors, a video will be generated and shown inline in the right panel.

**CRITICAL - VIDEO SAVING REALITY:**
- Videos are NOT automatically saved to the user's account
- The ONLY way to save a video is for the user to click **"Edit"** or **"Download"** from the right panel
- If the user leaves this chat without clicking Edit or Download, **the video will be lost**
- This chat interface does NOT persist between sessions

**NEVER tell users:**
- To "refresh the page" to see their video
- That their video is in their "drafts", "projects", or "recent videos"
- That you can create a "preview link" or "share link"
- That the video is saved but not "linked" to their account
- To log into Lumen5.com to find their video

**ALWAYS remind users:**
- To click "Edit" (to continue editing in the full editor) or "Download" (to save the video file) from the panel on the right
- That this is the only way to save their work
`;
}

export class SketchRootAgent extends RootAgent {
    private templateOptions: string = '';
    private userVideoHistory: string = '';
    private templateIds: string[] = [];
    private colorGradingFilters: ColorGradingFilter[] = [];
    private aiVoiceOptions: string = '';
    private knowledgeCutoff: string;
    private currentDate: string;
    private contextLoadingPromise: Promise<void>;
    private planFeatures: SketchPlanFeatures | null = null;

    constructor(config: RootAgentConfig) {
        super({
            ...config,
            requiresVideoState: false,
            maxIterations: config.maxIterations ?? 50,
            streaming: true,
        });

        // Initialize knowledge cutoff and current date based on the model
        this.knowledgeCutoff = getKnowledgeCutoff(config.model);
        this.currentDate = getCurrentDate();

        // Initialize reference material registry and subscribe to conversation
        const referenceMaterialRegistry = ReferenceMaterialRegistry.getInstance();
        referenceMaterialRegistry.clear();
        referenceMaterialRegistry.subscribeToConversation(this.conversation);

        // The system message will start with empty context and get updated when loaded
        this.contextLoadingPromise = this.loadSketchContext().catch((error) => {
            ErrorReporter.capture(error, {
                level: 'warning',
                extra: { message: 'Failed to load sketch context' },
            });
        });
    }

    /**
     * Returns a promise that resolves when the sketch context has finished loading.
     * Use this to wait for templates, AI voices, and user video history to be available.
     */
    public override waitForContext(): Promise<void> {
        return this.contextLoadingPromise;
    }

    /**
     * Updates the model and refreshes the knowledge cutoff and system message.
     */
    public override setModel(newModel: string): void {
        super.setModel(newModel);
        this.knowledgeCutoff = getKnowledgeCutoff(newModel);
        this.conversation.setSystemMessage(this.getSystemMessage());
    }

    private async loadSketchContext() {
        const data = await loadSketchAgentContext(this.dispatch, this.getState);
        this.templateOptions = data.templateOptions;
        this.userVideoHistory = data.userVideoHistory;
        this.templateIds = data.templateIds;
        this.colorGradingFilters = data.colorGradingFilters;
        this.aiVoiceOptions = data.aiVoiceOptions;
        this.planFeatures = data.planFeatures;

        // update the system message once context is loaded
        this.conversation.setSystemMessage(this.getSystemMessage());

        // Re-register tools with loaded template IDs (only if not using toolsOverride)
        if (!this.toolsOverride) {
            this.conversation.setTools(this.getTools(this.luminary, this.dispatch, this.getState));
        }
    }

    override getSystemMessage(): string {
        const canUseVoiceover = this.getState ? getCanUseAIVoiceover(this.getState()) : true;
        return getDefaultSystemMessage(
            this.templateOptions,
            this.userVideoHistory,
            this.aiVoiceOptions,
            this.knowledgeCutoff,
            this.currentDate,
            this.planFeatures,
            canUseVoiceover,
            true,
        );
    }

    public getUserPreferences(): string {
        return getSketchUserPreferences(this.getState);
    }

    override getTaskName(): string {
        return 'sketch';
    }

    public override getSkills(): Skill[] {
        return [];
    }

    async getContextMessage(): Promise<string | null> {
        const state = this.getState;
        if (!state) return null;

        const selectedVideoDraftId = selectSelectedVideoDraft(state());

        // If no video is explicitly selected, fallback to the most recently created video
        if (!selectedVideoDraftId) {
            const allDrafts = selectVideoDrafts(state());
            if (allDrafts.length === 0) {
                return 'There are no videos yet. Please create a video to get started.';
            }

            // Get the latest video
            const latestDraft = allDrafts[allDrafts.length - 1];
            return this.buildVideoContext(latestDraft, state());
        }

        const videoDraft = getVideoDraftById(state(), selectedVideoDraftId);
        if (!videoDraft) {
            return 'The selected video is not available. Please ensure the video exists.';
        }

        return this.buildVideoContext(videoDraft, state());
    }

    private async buildVideoContext(
        videoDraft: VideoDraft,
        state: RootState,
    ): Promise<string | null> {
        const mediaCandidates = getMediaCandidatesForAgent(state);
        const luminary = videoDraft.luminary;
        if (!luminary) return null;

        const result = await captureVideoStateContext(luminary, {
            mediaCandidates,
            getState: this.getState,
        });
        return result.description;
    }

    /**
     * PUBLIC API: Called from React when video is selected
     * Updates all tools to use the provided Luminary instance
     */
    public updateToolsWithLuminary(luminary: Luminary) {
        const allTools = this.getTools(luminary, this.dispatch, this.getState);
        this.conversation.setTools(allTools);
    }

    override getTools(luminary: Luminary, _dispatch: AppDispatch, _getState: GetRootState): Tool[] {
        // Creation tools - ALWAYS use sketch Luminary (blank canvas)
        const creationTools: Tool[] = [
            new CreateVideoTool({
                luminary: this.luminary,
                dispatch: this.dispatch,
                getState: this.getState,
                templateIds: this.templateIds,
                maxDurationSeconds: this.planFeatures?.maxVideoDurationSeconds,
            }),
            new CreateVideoFromScriptTool({
                luminary: this.luminary,
                dispatch: this.dispatch,
                getState: this.getState,
                templateIds: this.templateIds,
                maxDurationSeconds: this.planFeatures?.maxVideoDurationSeconds,
            }),
            new CreateVideoFromVoiceoverAssetTool({
                luminary: this.luminary,
                dispatch: this.dispatch,
                getState: this.getState,
                templateIds: this.templateIds,
            }),
            new CreateVideoFromPlanTool({
                luminary: this.luminary,
                dispatch: this.dispatch,
                getState: this.getState,
                templateIds: this.templateIds,
            }),
            new CreateVideoFromVoiceoverAssetPlanTool({
                luminary: this.luminary,
                dispatch: this.dispatch,
                getState: this.getState,
            }),
            new SketchUrlFetchTool({ luminary: this.luminary, dispatch: this.dispatch }),
            new SketchResearchTool({ luminary: this.luminary, dispatch: this.dispatch }),
            new SketchWebImageSearchTool({ luminary: this.luminary, dispatch: this.dispatch }),
        ];

        creationTools.push(
            new SuggestAnimatedExplainerTool({
                luminary: this.luminary,
                dispatch: this.dispatch,
            }),
        );

        // Edit tools - use selected video's Luminary passed via updateToolsWithLuminary
        const editTools = [
            new IconSearchTool({ luminary, dispatch: this.dispatch, getState: this.getState }),
            new LegacySetIconTool({ luminary, dispatch: this.dispatch }),
            new LegacySetBackgroundTool({ luminary, dispatch: this.dispatch }),
            new LegacySetAssetGroupTool({ luminary, dispatch: this.dispatch }),
            new MediaSearchTool({ luminary, dispatch: this.dispatch, getState: this.getState }),
            new DamMediaSearchTool({ luminary, dispatch: this.dispatch, getState: this.getState }),
            new GenerateAIMediaTool({ luminary, dispatch: this.dispatch }),
            new LegacyImageAskTool({ luminary, dispatch: this.dispatch }),
            new LegacySetMediaTool({ luminary, dispatch: this.dispatch, getState: this.getState }),
            new LegacySetStickerTool({
                luminary,
                dispatch: this.dispatch,
                getState: this.getState,
            }),
            new LegacySetSceneDurationTool({
                luminary,
                dispatch: this.dispatch,
                getState: this.getState,
            }),
            new LegacySetWatermarkTool({ luminary, dispatch: this.dispatch }),
            new LegacySetTextTool({ luminary, dispatch: this.dispatch, getState: this.getState }),
            new LegacySetTextPositionPresetTool({
                luminary,
                dispatch: this.dispatch,
                getState: this.getState,
            }),
            new SetAspectRatioTool({ luminary, dispatch: this.dispatch }),
            new LegacyAddSceneTool({
                luminary,
                dispatch: this.dispatch,
                getState: this.getState,
            }),
            // AddStickerTool is deliberately not registered here: it was disabled
            // for a rendering bug and has not been re-verified since.
            new LegacyAddAIVoiceoverSceneGroupTool({
                luminary,
                dispatch: this.dispatch,
                getState: this.getState,
            }),
            new LegacyDeleteSceneTool({
                luminary,
                dispatch: this.dispatch,
                getState: this.getState,
            }),
            new ListSceneDesignTool({
                luminary,
                dispatch: this.dispatch,
                getState: this.getState,
            }),
            new SetMusicTool({ luminary, dispatch: this.dispatch }),
            new SetMusicVolumeTool({ luminary, dispatch: this.dispatch }),
            new MusicSearchTool({ luminary, dispatch: this.dispatch }),
            new SetGlobalColorGradingTool({
                luminary,
                dispatch: this.dispatch,
                getState: this.getState,
                colorGradingFilters: this.colorGradingFilters,
            }),
            new SuggestUpgradeTool({ luminary, dispatch: this.dispatch }),
        ];

        const optionalTools: Tool[] = [];
        if (!this.model.startsWith('gpt-5')) {
            optionalTools.push(new CapabilityCheckTool({ luminary, dispatch: this.dispatch }));
        }

        return [...optionalTools, ...creationTools, ...editTools];
    }
}
